<template>
    <div>
        <form ref="form" action="/billing/subscribe" method="post" :disabled="!cardSetupComplete || !hasChangedPlan" @submit.prevent="onSubmit">
            <csrf></csrf>

            <div v-show="errors.length > 0" class="alert alert-bad">
                <ul>
                    <li v-for="error in errors" :key="error"><span class="fa fa-flag"></span>{{ error }}</li>
                </ul>
            </div>

            <div class="general-title">
                <div class="title">{{ hasCard ? 'Update' : 'Add'}} Subscription</div>
                <div class="buttons-container buttons">
                    <a href="/billing/history" class="neutral-bg">Go Back<span class="fa fa-arrow-left"></span></a>
                </div>
            </div>
            <div v-show="hasCard" class="input-container">
                <div class="input-title">Current Card</div>
                <div class="input-box static-text">{{ card.brand }} ****{{ card.last4 }}</div>
            </div>

            <div v-show="withCard">

                <div v-show="freeTrialAvailable" class="tool-tip add-buffer">
                    <div class="left">
                        <div class="headline">Your Free Trial</div>
                        You will not be charged until {{ firstBillingDate }}, when your 30-day trial ends. You may cancel your account on any date before that.
                    </div>
                </div>

                <div class="input-container">
                    <div class="input-title">Select Your Country</div>
                    <div class="input-box">
                        <selectize v-model="form.billing_country" name="billing_country" placeholder="Select Country" required>
                            <option v-for="country in countries" :key="country.value" :value="country.value">{{ country.name }}</option>
                        </selectize>
                    </div>
                </div>
                <div v-show="form.billing_country === 'USA'" class="input-container">
                    <div class="input-title">Select Your State</div>
                    <div class="input-box">
                        <selectize v-model="form.billing_state" name="billing_state" placeholder="Select State" required>
                            <option v-for="state in states" :key="state.value" :value="state.value">{{ state.name }}</option>
                        </selectize>
                    </div>
                </div>

                <input type="hidden" name="billing_zip" v-model="form.billing_zip">

                <card-form @completed="cardCompleted"></card-form>
            </div>

            <div class="input-container">
                <div class="input-title">How Many Children?</div>
                <label class="input-radio">
                    <input type="radio" name="max_children" value="4" v-model="form.max_children">
                    <span class="radio-checkmark"></span>
                    <div class="radio-text">Up to four children</div>
                </label>
                <label class="input-radio">
                    <input type="radio" name="max_children" value="6" v-model="form.max_children">
                    <span class="radio-checkmark"></span>
                    <div class="radio-text">Up to six children</div>
                </label>
            </div>
            <div v-show="canShowBillingInfo && !update" class="input-container">
                <div class="input-title">Choose Billing Rate</div>
                <label class="input-radio">
                    <input type="radio" name="billing_rate" value="YEARLY" v-model="form.billing_rate">
                    <span class="radio-checkmark"></span>
                    <div class="radio-text">${{ yearlyPricing }} {{ calculatedTax(yearlyPricing) }} per year</div>
                </label>
                <label class="input-radio">
                    <input type="radio" name="billing_rate" value="MONTHLY" v-model="form.billing_rate">
                    <span class="radio-checkmark"></span>
                    <div class="radio-text">${{ monthlyPricing }} {{ calculatedTax(monthlyPricing) }} per month</div>
                </label>
            </div>
            <div v-show="canShowBillingInfo && update" class="input-container">
                <div class="input-title">Billing Rate</div>
                <div class="input-box static-text">${{ billingPrice }} {{ calculatedTax(billingPrice) }} per {{ billingInterval.toLowerCase() }}</div>
            </div>
            <button v-show="!loading" type="submit" class="submit-standard good-bg" :disabled="!canShowBillingInfo || !hasChangedPlan">{{ isSubscribed ? 'Update' : 'Add' }} Subscription<span class="fa fa-users"></span></button>
            <button v-show="loading" type="submit" class="submit-standard neutral-bg" disabled>Processing...</button>
        </form>
    </div>
</template>

<script>

import SubscriptionMixin from '@/mixins/SubscriptionMixin';

export default {

    props: {

        card: {
            type: Object,
            default: () => ({}),
        },

        account: {
            type: Object,
            required: true,
        },

        withCard: {
            type: Boolean,
            default: false,
        },

        isSubscribed: {
            type: Boolean,
            required: true,
        },

        currentBillingRate: {
            type: String,
            default: 'MONTHLY',
        },

        freeTrialAvailable: {
            type: Boolean,
            default: false,
        },

        update: {
            type: Boolean,
            default: false,
        },

    },

    mixins: [ SubscriptionMixin ],

    data() {
        return {
            loading: false,
            errors: [],
            type: 'STANDARD',
            current_max_children: this.account.max_children_allowed,
            form: {
                max_children: this.account.max_children_allowed || 4,
                billing_country: this.account.billing_country,
                billing_state: this.account.billing_state,
                billing_zip: this.account.billing_zip,
                billing_rate: this.currentBillingRate,
            },
        };
    },

    computed: {

        hasCard() {
            return this.card && this.card.last4;
        },

        hasChangedPlan() {
            return parseInt(this.current_max_children, 10) != parseInt(this.form.max_children, 10);
        },

        billingInterval() {
            switch (this.form.billing_rate) {
                case 'YEARLY':
                    return 'Year';
                case 'MONTHLY':
                default:
                    return 'Month';
            }
        },

        billingPrice() {
            switch (this.form.billing_rate) {
                case 'YEARLY':
                    return this.yearlyPricing;
                case 'MONTHLY':
                default:
                    return this.monthlyPricing;
            }
        },

    },

    mounted() {
        if (this.form.billing_state && this.form.billing_zip) {
            this.cardSetupComplete = true;
            this.getTaxRate();
        }
    },

    methods: {

        async onSubmit() {
            this.loading = true;
            this.errors = [];

            try {
                const { data } = await axios.post(this.update ? '/billing/update-subscription' : '/billing', new FormData(this.$refs.form));

                if (data.success) {
                    this.handleSuccessfulSubscription();
                }
            } catch (e) {
                if (e.response && e.response.data && e.response.data.errors) {
                    Object.values(e.response.data.errors).forEach(errors => {
                        errors.forEach(error => {
                            this.errors.push(error);
                        });
                    });

                    document.querySelector('html').scrollTop = 0;
                } else {
                    this.errors = [ apiErrors.getErrorMessage(e) ];

                    document.querySelector('html').scrollTop = 0;
                }
            }

            this.loading = false;
        },

        handleSuccessfulSubscription() {
            return window.location.href = '/billing/history';
        },

    }

}

</script>



// WEBPACK FOOTER //
// resources/js/components/billing/ManageBilling.vue